import React, { useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import {
  School, Users, HeartHandshake, Church, Building2, Baby, Sparkles,
  CheckCircle2, Calendar, BookOpen, Compass, Award, ArrowRight,
} from "lucide-react";
import { API } from "@/lib/config";

const AUDIENCES = [
  { icon: School, label: "School Principals" },
  { icon: Users, label: "School Counselors" },
  { icon: Building2, label: "Group Home Directors" },
  { icon: Baby, label: "Foster Care Agencies" },
  { icon: Church, label: "Churches" },
  { icon: HeartHandshake, label: "Community Organizations" },
  { icon: Sparkles, label: "Youth Programs" },
];

const PROGRAMS = [
  { tag: "SHE BELIEVES", title: "Confidence Development", desc: "Self-worth, emotional resilience, voice, and public speaking." },
  { tag: "SHE GROWS", title: "Wellness & Self-Care", desc: "Emotional wellness, hygiene education, and healthy relationships." },
  { tag: "SHE LEADS", title: "Life Readiness", desc: "Financial literacy, career exploration, etiquette, and goal setting." },
];

const OUTCOMES = [
  "Increased confidence and self-worth",
  "Stronger communication and leadership skills",
  "Healthier relationships and emotional wellness",
  "Clearer vision for college and careers",
  "Practical life-readiness skills",
  "A supportive sisterhood and positive mentors",
];

const BENEFITS = [
  { icon: BookOpen, title: "Turnkey Curriculum", desc: "Facilitator-led workshops ready to run in your space." },
  { icon: Compass, title: "Tailored to Your Girls", desc: "Programming adapted to your community's needs." },
  { icon: Award, title: "Trained Mentors", desc: "Vetted, caring women who show up consistently." },
  { icon: Calendar, title: "Flexible Scheduling", desc: "Single events, series, or ongoing partnerships." },
];

const PARTNER_TYPES = [
  "School / School District",
  "Group Home",
  "Foster Care Agency",
  "Church / Faith Community",
  "Community Organization",
  "Youth Program",
  "Corporate Sponsor",
  "Other",
];

const SchoolsPage = () => {
  const [form, setForm] = useState({
    organization: "", contact_name: "", email: "", phone: "",
    partner_type: PARTNER_TYPES[0], message: "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/partner`, form);
      toast.success("Thank you! Your partnership inquiry has been received. Our team will be in touch shortly.");
      setForm({ organization: "", contact_name: "", email: "", phone: "", partner_type: PARTNER_TYPES[0], message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or email info@shesagirlsgirl.org.");
    } finally {
      setLoading(false);
    }
  };

  const field = (k, v) => setForm({ ...form, [k]: v });
  const inputCls = "bg-white border border-[#E5E5E5] rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E94F9A]/40";

  return (
    <div className="py-24 max-w-6xl mx-auto px-6 text-left space-y-24 animate-fade-in" data-testid="schools-page">
      {/* Hero */}
      <div className="text-center max-w-2xl mx-auto space-y-5">
        <span className="font-sans text-xs font-semibold uppercase tracking-widest text-[#C8A96A]">{"For Schools & Organizations"}</span>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#4B1E5A] agg-heading agg-heading-center">
          {"Bring AGG to Your Girls"}
        </h1>
        <p className="font-sans text-base text-[#4A4A4A] leading-relaxed">
          {"A Girl's Girl partners with schools, group homes, and community organizations to deliver confidence, leadership, and life-readiness programming where girls already are."}
        </p>
        <div className="pt-2">
          <a href="#partnership-form" data-testid="schools-hero-cta" className="inline-flex items-center gap-2 bg-[#E94F9A] text-white rounded-full px-8 py-4 text-sm font-semibold hover:bg-[#D13D86] shadow-md hover:-translate-y-0.5 transition-all">
            {"Request a Partnership"} <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </div>

      {/* Who we serve */}
      <div className="space-y-8">
        <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#4B1E5A] text-center">{"Who We Partner With"}</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {AUDIENCES.map((a) => (
            <div key={a.label} className="flex items-center gap-3 bg-white border border-[#E5E5E5]/60 rounded-2xl p-4 hover:border-[#E94F9A]/30 hover:-translate-y-0.5 transition-all">
              <div className="h-10 w-10 rounded-xl bg-[#FFF5F9] flex items-center justify-center flex-shrink-0">
                <a.icon className="h-5 w-5 text-[#E94F9A]" />
              </div>
              <span className="font-sans text-sm font-medium text-[#4B1E5A] leading-snug">{a.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Programs */}
      <div className="space-y-8">
        <div className="text-center space-y-2 max-w-xl mx-auto">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#4B1E5A]">{"What AGG Offers"}</h2>
          <p className="font-sans text-sm text-[#4A4A4A]">{"Three signature program pillars, ready to bring to your community."}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PROGRAMS.map((p) => (
            <div key={p.tag} className="bg-white border border-[#E5E5E5]/60 rounded-3xl p-7 space-y-3 hover:shadow-lg transition-all">
              <span className="inline-block bg-[#FFF5F9] text-[#E94F9A] text-[11px] font-bold px-3 py-1 rounded-full tracking-widest uppercase">{p.tag}</span>
              <h3 className="font-serif text-lg font-bold text-[#4B1E5A]">{p.title}</h3>
              <p className="font-sans text-sm text-[#4A4A4A] leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Outcomes + Benefits */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div className="bg-[#FFF5F9]/30 border border-[#FFF5F9] rounded-[2.5rem] p-8 md:p-10 space-y-5">
          <h2 className="font-serif text-2xl font-bold text-[#4B1E5A]">{"Outcomes for Your Girls"}</h2>
          <div className="space-y-3">
            {OUTCOMES.map((o) => (
              <div key={o} className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-[#E94F9A] flex-shrink-0 mt-0.5" />
                <span className="font-sans text-sm text-[#4A4A4A]">{o}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-5">
          <h2 className="font-serif text-2xl font-bold text-[#4B1E5A]">{"Partnership Benefits"}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {BENEFITS.map((b) => (
              <div key={b.title} className="bg-white border border-[#E5E5E5]/60 rounded-2xl p-5 space-y-2">
                <div className="h-10 w-10 rounded-xl bg-[#FFF5F9] flex items-center justify-center">
                  <b.icon className="h-5 w-5 text-[#E94F9A]" />
                </div>
                <h3 className="font-serif text-sm font-bold text-[#4B1E5A]">{b.title}</h3>
                <p className="font-sans text-xs text-[#4A4A4A] leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Partnership Inquiry Form */}
      <div id="partnership-form" className="bg-white border border-[#E5E5E5]/50 shadow-md rounded-[3rem] p-8 md:p-12 max-w-3xl mx-auto scroll-mt-28" data-testid="partnership-form-section">
        <div className="text-center space-y-2 mb-8">
          <span className="font-sans text-xs font-semibold uppercase tracking-widest text-[#C8A96A]">{"Let's Talk"}</span>
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#4B1E5A]">{"Partnership Inquiry"}</h2>
          <p className="font-sans text-sm text-[#4A4A4A]">{"Tell us about your organization and we'll reach out to design a program for your girls."}</p>
        </div>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="partnership-inquiry-form">
          <div className="flex flex-col">
            <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">{"Organization"}</label>
            <input type="text" value={form.organization} onChange={(e) => field("organization", e.target.value)} required data-testid="partner-input-organization" placeholder="e.g. Charlotte-Mecklenburg Schools" className={inputCls} />
          </div>
          <div className="flex flex-col">
            <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">{"Contact Name"}</label>
            <input type="text" value={form.contact_name} onChange={(e) => field("contact_name", e.target.value)} required data-testid="partner-input-name" placeholder="e.g. Dr. Arlene Henderson" className={inputCls} />
          </div>
          <div className="flex flex-col">
            <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">{"Email"}</label>
            <input type="email" value={form.email} onChange={(e) => field("email", e.target.value)} required data-testid="partner-input-email" placeholder="you@organization.org" className={inputCls} />
          </div>
          <div className="flex flex-col">
            <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">{"Phone"}</label>
            <input type="tel" value={form.phone} onChange={(e) => field("phone", e.target.value)} required data-testid="partner-input-phone" placeholder="704-555-0100" className={inputCls} />
          </div>
          <div className="flex flex-col sm:col-span-2">
            <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">{"Organization Type"}</label>
            <select value={form.partner_type} onChange={(e) => field("partner_type", e.target.value)} data-testid="partner-input-type" className={inputCls}>
              {PARTNER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="flex flex-col sm:col-span-2">
            <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">{"How can we serve your girls?"}</label>
            <textarea value={form.message} onChange={(e) => field("message", e.target.value)} required rows={4} data-testid="partner-input-message" placeholder="Tell us about your girls, your goals, and any timing in mind." className={inputCls} />
          </div>
          <div className="sm:col-span-2 text-center pt-2">
            <button type="submit" disabled={loading} data-testid="partner-submit-btn" className="bg-[#E94F9A] hover:bg-[#D13D86] text-white rounded-full px-12 py-3.5 text-sm font-semibold transition-all shadow-md disabled:opacity-60">
              {loading ? "Sending..." : "Submit Partnership Inquiry"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SchoolsPage;
